import React, { useState } from 'react';
import { 
  CloudRain, 
  Droplets, 
  Sun, 
  Wind, 
  Zap, 
  TrendingUp, 
  Calendar, 
  AlertTriangle,
  Smartphone,
  Leaf
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Card, Badge } from '../ui';

interface MicroForecastProps {
  farmLocation?: string;
}

// Sample data - would be replaced with real API data
const forecastData = {
  current: {
    date: new Date(),
    rainfall: 0.2,
    temperature: 28,
    humidity: 72,
    soilMoisture: 65,
    recommendation: "Ideal conditions for coffee flowering. Consider light irrigation in sectors A and C."
  },
  upcoming: [
    {
      date: new Date(Date.now() + 86400000), // tomorrow
      rainfall: 12.5,
      temperature: 26,
      humidity: 85,
      soilMoisture: 75,
      alert: "Heavy rain expected",
      recommendation: "Delay fertilizer application by 48 hours to prevent runoff."
    },
    {
      date: new Date(Date.now() + 172800000), // day after tomorrow
      rainfall: 3.2,
      temperature: 27,
      humidity: 80,
      soilMoisture: 80,
      recommendation: "Good conditions for pest monitoring in banana plots."
    },
    {
      date: new Date(Date.now() + 259200000), // 3 days from now
      rainfall: 0,
      temperature: 29,
      humidity: 65,
      soilMoisture: 70,
      recommendation: "Consider harvesting mature coffee cherries."
    }
  ],
  alerts: [
    {
      type: "weather",
      severity: "medium",
      message: "Heavy rainfall expected tomorrow may affect coffee drying",
      action: "Ensure coffee drying tables are covered"
    }
  ],
  smsNotifications: true
};

const MicroForecast: React.FC<MicroForecastProps> = ({ farmLocation = "Jinja, Uganda" }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [activeDay, setActiveDay] = useState(0);

  return (
    <Card
      title="Micro-Climate Forecast"
      subtitle={`Personalized for your farm in ${farmLocation}`}
      icon={
        <motion.div
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{ duration: 4, repeat: Infinity }}
        >
          <CloudRain className="h-5 w-5 text-blue-600" />
        </motion.div>
      }
      footer={
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Smartphone className="h-4 w-4 text-avn-purple-600" />
            <span>
              {forecastData.smsNotifications 
                ? "SMS alerts enabled" 
                : "SMS alerts disabled"}
            </span>
          </div>
          <button className="text-avn-purple-600 hover:text-avn-purple-700">
            Manage Notifications
          </button>
        </div>
      }
    >
      {/* Current Conditions */}
      <div className="bg-blue-50 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Sun className="h-6 w-6 text-amber-500" />
            <span className="font-medium">Current Conditions</span>
          </div>
          <span className="text-sm text-gray-500">
            {forecastData.current.date.toLocaleDateString()}
          </span>
        </div>
        
        <div className="grid grid-cols-4 gap-4 mb-4">
          <div className="text-center">
            <div className="text-2xl font-bold">{forecastData.current.temperature}°C</div>
            <div className="text-xs text-gray-600">Temperature</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{forecastData.current.humidity}%</div>
            <div className="text-xs text-gray-600">Humidity</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{forecastData.current.rainfall}mm</div>
            <div className="text-xs text-gray-600">Rainfall</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold">{forecastData.current.soilMoisture}%</div>
            <div className="text-xs text-gray-600">Soil Moisture</div>
          </div>
        </div>
        
        <div className="bg-white p-3 rounded-lg text-sm">
          <div className="flex items-center gap-2 text-avn-purple-600 mb-1">
            <Zap className="h-4 w-4" />
            <span className="font-medium">AI Recommendation</span>
          </div>
          <p className="text-gray-700">{forecastData.current.recommendation}</p>
        </div>
      </div>

      {/* Upcoming Forecast */}
      <div className="mb-6">
        <h3 className="font-medium mb-3">3-Day Micro-Forecast</h3>
        <div className="grid grid-cols-3 gap-3">
          {forecastData.upcoming.map((day, index) => (
            <div 
              key={index}
              onClick={() => setActiveDay(index)}
              className={`p-3 rounded-lg cursor-pointer transition-colors ${
                activeDay === index 
                  ? 'bg-avn-purple-100 border border-avn-purple-200' 
                  : 'bg-gray-50 hover:bg-gray-100'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">
                  {day.date.toLocaleDateString(undefined, { weekday: 'short' })}
                </span>
                {day.alert && (
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                )}
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <CloudRain className="h-4 w-4 text-blue-500" />
                  <span className="text-sm">{day.rainfall}mm</span>
                </div>
                <div className="text-sm font-medium">{day.temperature}°C</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Selected Day Details */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium">
            {forecastData.upcoming[activeDay].date.toLocaleDateString(undefined, { 
              weekday: 'long', 
              month: 'short', 
              day: 'numeric' 
            })}
          </h3>
          {forecastData.upcoming[activeDay].alert && (
            <Badge variant="amber" icon={<AlertTriangle className="h-3 w-3" />}>
              {forecastData.upcoming[activeDay].alert}
            </Badge>
          )}
        </div>
        
        <div className="grid grid-cols-4 gap-4 mb-4 text-center text-sm">
          <div>
            <Sun className="h-5 w-5 mx-auto mb-1 text-amber-500" />
            <div className="font-medium">{forecastData.upcoming[activeDay].temperature}°C</div>
            <div className="text-xs text-gray-500">Temperature</div>
          </div>
          <div>
            <CloudRain className="h-5 w-5 mx-auto mb-1 text-blue-500" />
            <div className="font-medium">{forecastData.upcoming[activeDay].rainfall}mm</div>
            <div className="text-xs text-gray-500">Rainfall</div>
          </div>
          <div>
            <Wind className="h-5 w-5 mx-auto mb-1 text-gray-500" />
            <div className="font-medium">{forecastData.upcoming[activeDay].humidity}%</div>
            <div className="text-xs text-gray-500">Humidity</div>
          </div>
          <div>
            <Droplets className="h-5 w-5 mx-auto mb-1 text-blue-500" />
            <div className="font-medium">{forecastData.upcoming[activeDay].soilMoisture}%</div>
            <div className="text-xs text-gray-500">Soil Moisture</div>
          </div>
        </div>
        
        <div className="bg-white p-3 rounded-lg text-sm">
          <div className="flex items-center gap-2 text-avn-purple-600 mb-1">
            <TrendingUp className="h-4 w-4" />
            <span className="font-medium">Farming Recommendation</span>
          </div>
          <p className="text-gray-700">{forecastData.upcoming[activeDay].recommendation}</p>
        </div>
      </div>

      {/* Alerts */}
      {forecastData.alerts.length > 0 && (
        <div className="mb-6">
          <h3 className="font-medium mb-3">Active Alerts</h3>
          {forecastData.alerts.map((alert, index) => (
            <div key={index} className="bg-amber-50 border border-amber-100 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="font-medium">{alert.message}</span>
              </div>
              <p className="text-sm text-amber-700 ml-6">
                Recommended action: {alert.action}
              </p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default MicroForecast;